import { useEffect, useState } from 'react';
import { consultationsService } from '../services/api';
import './css/AttachmentPreviewModal.css';

const AttachmentPreviewModal = ({ consultationId, attachmentId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [attachmentInfo, setAttachmentInfo] = useState(null);

  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;

    const loadAttachment = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get the attachment URL with authentication
        const url = consultationsService.getAttachmentUrl(consultationId, attachmentId);
        
        // Fetch with authentication to get blob
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load attachment');
        }

        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'attachment';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match) {
            filename = match[1];
          }
        }

        const contentType = response.headers.get('Content-Type');
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }

        setAttachmentUrl(blobUrl);
        setAttachmentInfo({
          filename,
          contentType,
          size: blob.size
        });
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading attachment:', err);
          setError('Nie udało się załadować załącznika');
          setLoading(false);
        }
      }
    };

    loadAttachment();

    // Cleanup blob URL on unmount
    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [consultationId, attachmentId]);

  const handleDownload = () => {
    if (attachmentUrl && attachmentInfo) {
      const link = document.createElement('a');
      link.href = attachmentUrl;
      link.download = attachmentInfo.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBackdropClick = (e) => {
    // Close modal only if clicking on the backdrop, not the content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isImage = attachmentInfo?.contentType?.startsWith('image/');
  const isPdf = attachmentInfo?.contentType === 'application/pdf';

  return (
    <div className="attachment-modal-overlay" onClick={handleBackdropClick}>
      <div className="attachment-modal-content">
        <div className="attachment-modal-header">
          <div className="attachment-modal-info">
            <h3>{attachmentInfo?.filename || 'Załącznik'}</h3>
            {attachmentInfo?.size && (
              <span className="attachment-modal-size">
                {(attachmentInfo.size / 1024).toFixed(1)} KB
              </span>
            )}
          </div>
          <div className="attachment-modal-actions">
            {attachmentUrl && (
              <button className="attachment-modal-download-btn" onClick={handleDownload} title="Pobierz">
                ⬇
              </button>
            )}
            <button className="attachment-modal-close-btn" onClick={onClose} title="Zamknij">
              ✕
            </button>
          </div>
        </div>

        <div className="attachment-modal-body">
          {loading && (
            <div className="attachment-modal-loading">
              <div className="spinner"></div>
              <p>Ładowanie załącznika...</p>
            </div>
          )}

          {error && (
            <div className="attachment-modal-error">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && attachmentUrl && (
            <>
              {isImage && (
                <div className="attachment-modal-image-preview">
                  <img src={attachmentUrl} alt={attachmentInfo?.filename} />
                </div>
              )}
              {isPdf && (
                <div className="attachment-modal-iframe-preview">
                  <iframe
                    src={attachmentUrl}
                    title={attachmentInfo?.filename}
                    width="100%"
                    height="100%"
                  />
                </div>
              )}
              {!isImage && !isPdf && (
                <div className="attachment-modal-unsupported">
                  <div className="unsupported-icon">📎</div>
                  <p>Podgląd niedostępny dla tego typu pliku</p>
                  <button className="attachment-modal-download-btn-large" onClick={handleDownload}>
                    ⬇ Pobierz plik
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreviewModal;
